import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mic, Square, Play, Pause, Link2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VoiceNoteCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isEditor, currentUser } = useUser();
  
  // Get activity_id from URL if coming from Activities page
  const linkedActivityId = searchParams.get('activity_id');
  const preselectedCompanyId = searchParams.get('company_id');
  
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [linkedActivity, setLinkedActivity] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedContact, setSelectedContact] = useState('none');
  const [selectedOpportunity, setSelectedOpportunity] = useState('none');
  const [creator, setCreator] = useState('');
  const [typsaCategory, setTypsaCategory] = useState('');
  const [typsaCategoryValue, setTypsaCategoryValue] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioURL, setAudioURL] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [loading, setLoading] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(''); // '', 'uploading', 'transcribing', 'processing', 'linking'
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    // Redirect viewers away from creation page
    if (!isEditor) {
      toast.error('No tienes permisos para crear notas de voz');
      navigate('/voice-notes');
      return;
    }
    fetchData();
    // Auto-fill creator with current user
    if (currentUser?.name) {
      setCreator(currentUser.name);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isEditor, navigate, currentUser]);

  // Fetch linked activity info
  useEffect(() => {
    if (linkedActivityId) {
      fetchLinkedActivity();
    }
  }, [linkedActivityId]);

  // Pre-select company from URL
  useEffect(() => {
    if (preselectedCompanyId && companies.length > 0) {
      setSelectedCompany(preselectedCompanyId);
    } else if (companies.length > 0 && !selectedCompany) {
      setSelectedCompany(companies[0].id);
    }
  }, [preselectedCompanyId, companies]);

  // Reset opportunity and contact when company changes
  useEffect(() => {
    if (!linkedActivityId) {
      setSelectedOpportunity('none');
      setSelectedContact('none');
    }
    setTypsaCategory('');
    setTypsaCategoryValue('');
  }, [selectedCompany, linkedActivityId]);

  const fetchLinkedActivity = async () => {
    try {
      const response = await axios.get(`${API}/activities/${linkedActivityId}`);
      setLinkedActivity(response.data);
      // Pre-fill fields from activity
      if (response.data.company_id) {
        setSelectedCompany(response.data.company_id);
      }
      if (response.data.contact_id) {
        setSelectedContact(response.data.contact_id);
      }
      if (response.data.opportunity_id) {
        setSelectedOpportunity(response.data.opportunity_id);
      }
    } catch (error) {
      console.error('Error fetching linked activity:', error);
      toast.error('No se pudo cargar la actividad vinculada');
    }
  };

  const fetchData = async () => {
    try {
      const [companiesRes, contactsRes, oppsRes] = await Promise.all([
        axios.get(`${API}/companies`),
        axios.get(`${API}/contacts`),
        axios.get(`${API}/opportunities`)
      ]);
      setCompanies(companiesRes.data);
      setContacts(contactsRes.data);
      setOpportunities(oppsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // Filter opportunities by selected company
  const filteredOpportunities = opportunities.filter(
    opp => opp.company_id === selectedCompany
  );

  // Filter contacts by selected company
  const filteredContacts = contacts.filter(
    c => c.company_id === selectedCompany
  );

  // Check if selected company is TYPSA
  const selectedCompanyData = companies.find(c => c.id === selectedCompany);
  const isTypsaCompany = selectedCompanyData?.name?.toUpperCase().includes('TYPSA');

  // Handle contact change - auto-fill TYPSA category
  const handleContactChange = (value) => {
    const newContactId = value === 'none' ? '' : value;
    setSelectedContact(value);
    
    if (isTypsaCompany && newContactId) {
      const contact = contacts.find(c => c.id === newContactId);
      if (contact?.typsa_category && contact?.typsa_category_value) {
        setTypsaCategory(contact.typsa_category);
        setTypsaCategoryValue(contact.typsa_category_value);
      } else {
        setTypsaCategory('');
        setTypsaCategoryValue('');
      }
    } else {
      setTypsaCategory('');
      setTypsaCategoryValue('');
    }
  };

  const startRecording = async () => {
    try {
      // Request microphone with specific constraints for better quality
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
          channelCount: 1
        } 
      });
      
      // Try to use a more compatible audio format - prioritize formats that work well with Whisper
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus'
      ];
      
      let selectedMimeType = mimeTypes.find(type => MediaRecorder.isTypeSupported(type));
      
      if (!selectedMimeType) {
        // Fallback to default if none supported
        selectedMimeType = 'audio/webm';
        console.warn('No preferred MIME type supported, using default:', selectedMimeType);
      }
      
      console.log('Using audio format:', selectedMimeType);
      console.log('MediaRecorder supported:', MediaRecorder.isTypeSupported(selectedMimeType));
      
      // Store stream reference to stop tracks later
      const streamRef = stream;
      
      mediaRecorderRef.current = new MediaRecorder(stream, { 
        mimeType: selectedMimeType,
        audioBitsPerSecond: 128000
      });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        console.log('Data available event, size:', event.data.size);
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        console.log('Recording stopped, chunks count:', audioChunksRef.current.length);
        
        if (audioChunksRef.current.length === 0) {
          toast.error('No se capturó audio. Por favor intente de nuevo.');
          streamRef.getTracks().forEach(track => track.stop());
          return;
        }
        
        const blob = new Blob(audioChunksRef.current, { type: selectedMimeType.split(';')[0] });
        console.log('Recording complete, blob size:', blob.size, 'type:', blob.type);
        
        if (blob.size < 1000) {
          toast.error('El audio grabado es muy pequeño. Por favor grabe durante más tiempo.');
          streamRef.getTracks().forEach(track => track.stop());
          return;
        }
        
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioURL(url);
        streamRef.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        toast.error('Error durante la grabación: ' + (event.error?.message || 'Error desconocido'));
        streamRef.getTracks().forEach(track => track.stop());
      };

      // Request data every 500ms to ensure we capture data more frequently
      mediaRecorderRef.current.start(500);
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.success('Grabación iniciada');
    } catch (error) {
      console.error('Error accessing microphone:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Permiso de micrófono denegado. Por favor habilite el micrófono en la configuración del navegador.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No se encontró ningún micrófono. Por favor conecte un micrófono.');
      } else {
        toast.error('No se pudo acceder al micrófono: ' + error.message);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      // Check minimum recording time (at least 2 seconds)
      if (recordingTime < 2) {
        toast.warning('Grabe al menos 2 segundos de audio');
        return;
      }
      
      console.log('Stopping recording after', recordingTime, 'seconds');
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const togglePlayback = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSave = async () => {
    if (!selectedCompany && !linkedActivityId) {
      toast.error('Debe seleccionar una empresa');
      return;
    }

    if (!audioBlob) {
      toast.error('Debe grabar un audio');
      return;
    }
    
    // Validate minimum audio size (at least 5KB for a meaningful recording)
    const minAudioSize = 5 * 1024; // 5KB
    if (audioBlob.size < minAudioSize) {
      toast.error(`El audio es demasiado corto (${(audioBlob.size/1024).toFixed(1)}KB). Por favor grabe al menos 2-3 segundos de audio.`);
      return;
    }
    
    console.log('Audio blob size:', audioBlob.size, 'bytes');

    setLoading(true);
    try {
      // Step 1: Upload audio
      setProcessingStatus('uploading');
      const formData = new FormData();
      formData.append('file', audioBlob, 'voice-note.webm');
      
      const uploadRes = await axios.post(`${API}/voice-notes/upload-audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Step 2: Create voice note
      const voiceNoteData = {
        date_time: new Date().toISOString(),
        company_id: selectedCompany || linkedActivity?.company_id || null,
        contact_id: selectedContact === 'none' ? null : selectedContact,
        opportunity_id: selectedOpportunity === 'none' ? null : selectedOpportunity,
        creator: creator || null,
        audio_url: uploadRes.data.audio_url,
        typsa_category: typsaCategory || null,
        typsa_category_value: typsaCategoryValue || null
      };

      const noteRes = await axios.post(`${API}/voice-notes`, voiceNoteData);
      const voiceNoteId = noteRes.data.id;
      
      // If linked to activity, auto-process and link
      if (linkedActivityId) {
        toast.success('Nota de voz guardada. Procesando...');
        
        const headers = { 'x-user-email': currentUser?.email };
        
        // Step 3: Transcribe
        setProcessingStatus('transcribing');
        await axios.post(`${API}/voice-notes/${voiceNoteId}/transcribe`, {}, { headers });
        
        // Step 4: Process with AI
        setProcessingStatus('processing');
        await axios.post(`${API}/voice-notes/${voiceNoteId}/process`);
        
        // Step 5: Link to activity
        setProcessingStatus('linking');
        const activityType = linkedActivity?.type || 'meeting';
        const activityTitle = linkedActivity?.title || 'Actividad documentada';
        
        await axios.post(
          `${API}/voice-notes/${voiceNoteId}/create-activity?activity_id=${linkedActivityId}&activity_type=${activityType}&title=${encodeURIComponent(activityTitle)}`,
          {},
          { headers }
        );
        
        toast.success('Actividad documentada correctamente');
        navigate('/activities');
      } else {
        toast.success('Nota de voz guardada');
        navigate(`/voice-notes/${voiceNoteId}`);
      }
    } catch (error) {
      console.error('Error saving voice note:', error);
      const errorMsg = error.response?.data?.detail || 'Error al guardar la nota de voz';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
      setProcessingStatus('');
    }
  };

  const getProcessingMessage = () => {
    switch (processingStatus) {
      case 'uploading': return 'Subiendo audio...';
      case 'transcribing': return 'Transcribiendo...';
      case 'processing': return 'Analizando con IA...';
      case 'linking': return 'Vinculando a actividad...';
      default: return 'Guardando...';
    }
  };

  return (
    <div className="p-4 md:p-8" data-testid="voice-note-create-page">
      <Button
        variant="ghost"
        onClick={() => linkedActivityId ? navigate('/activities') : navigate('/voice-notes')}
        className="mb-4"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {linkedActivityId ? 'Volver a Actividades' : 'Volver'}
      </Button>

      <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
        {linkedActivityId ? 'Documentar Actividad' : 'Nueva Nota de Voz'}
      </h1>

      {/* Linked Activity Banner */}
      {linkedActivity && (
        <Card className="mb-6 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Link2 className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Documentando actividad:
                </p>
                <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                  {linkedActivity.title || `${linkedActivity.type} - ${linkedActivity.date}`}
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  {linkedActivity.date} · Al guardar, se transcribirá, procesará y vinculará automáticamente
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Only show company selector if NOT linked to activity */}
        {!linkedActivityId && (
          <Card>
            <CardHeader>
              <CardTitle>Información</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="company">Empresa *</Label>
                <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                  <SelectTrigger data-testid="voice-note-company-select">
                    <SelectValue placeholder="Seleccionar empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="opportunity">Oportunidad (opcional)</Label>
                <Select 
                  value={selectedOpportunity} 
                  onValueChange={setSelectedOpportunity}
                  disabled={!selectedCompany}
                >
                  <SelectTrigger data-testid="voice-note-opportunity-select">
                    <SelectValue placeholder="Seleccionar oportunidad" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguna</SelectItem>
                  {filteredOpportunities.length === 0 ? (
                    <SelectItem value="no-opps" disabled>
                      No hay oportunidades para esta empresa
                    </SelectItem>
                  ) : (
                    filteredOpportunities.map((opp) => (
                      <SelectItem key={opp.id} value={opp.id}>
                        {opp.title || opp.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {selectedCompany && filteredOpportunities.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  No hay oportunidades asociadas a esta empresa
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="contact">Contacto (opcional)</Label>
              <Select 
                value={selectedContact} 
                onValueChange={handleContactChange}
                disabled={!selectedCompany}
              >
                <SelectTrigger data-testid="voice-note-contact-select">
                  <SelectValue placeholder="Seleccionar contacto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ninguno</SelectItem>
                  {filteredContacts.length === 0 ? (
                    <SelectItem value="no-contacts" disabled>
                      No hay contactos para esta empresa
                    </SelectItem>
                  ) : (
                    filteredContacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.first_name || contact.name} {contact.last_name || ''}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* TYPSA Category - shown when TYPSA is selected and contact has category */}
            {isTypsaCompany && typsaCategory && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <Label className="text-blue-700 dark:text-blue-300 text-xs uppercase tracking-wider">
                  Clasificación TYPSA (del contacto)
                </Label>
                <div className="mt-1 font-medium text-blue-800 dark:text-blue-200">
                  {typsaCategory === 'division' && 'División'}
                  {typsaCategory === 'departamento' && 'Departamento'}
                  {typsaCategory === 'territorio' && 'Territorio'}
                  {typsaCategory === 'grupo' && 'Grupo'}
                  : {typsaCategoryValue}
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="creator">Creador *</Label>
              <Input
                id="creator"
                value={creator}
                onChange={(e) => setCreator(e.target.value)}
                placeholder="Nombre del creador"
                data-testid="voice-note-creator-input"
              />
            </div>
          </CardContent>
        </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Grabación</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col items-center justify-center py-8 space-y-6">
              {!audioURL && (
                <>
                  <div className={`w-24 h-24 rounded-full flex items-center justify-center ${
                    isRecording ? 'bg-red-100 animate-pulse' : 'bg-gray-100'
                  }`}>
                    <Mic className={`w-12 h-12 ${isRecording ? 'text-red-500' : 'text-gray-400'}`} />
                  </div>
                  
                  {isRecording && (
                    <div className="text-lg font-medium">{formatTime(recordingTime)}</div>
                  )}

                  <Button
                    onClick={isRecording ? stopRecording : startRecording}
                    variant={isRecording ? "destructive" : "default"}
                    size="lg"
                    data-testid="record-button"
                  >
                    {isRecording ? (
                      <>
                        <Square className="w-5 h-5 mr-2" />
                        Detener
                      </>
                    ) : (
                      <>
                        <Mic className="w-5 h-5 mr-2" />
                        Grabar
                      </>
                    )}
                  </Button>
                </>
              )}

              {audioURL && (
                <>
                  <audio
                    ref={audioRef}
                    src={audioURL}
                    onEnded={() => setIsPlaying(false)}
                    className="w-full"
                    controls
                  />
                  
                  <div className="flex gap-4">
                    <Button
                      onClick={() => {
                        setAudioBlob(null);
                        setAudioURL('');
                        setRecordingTime(0);
                      }}
                      variant="outline"
                    >
                      Grabar de nuevo
                    </Button>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            variant="outline"
            onClick={() => linkedActivityId ? navigate('/activities') : navigate('/voice-notes')}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading || !audioBlob || (!selectedCompany && !linkedActivityId)}
            className="w-full sm:w-auto"
            data-testid="save-voice-note-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {getProcessingMessage()}
              </>
            ) : linkedActivityId ? (
              'Guardar y documentar'
            ) : (
              'Guardar'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VoiceNoteCreate;
